import Grid, { GridProps } from '@material-ui/core/Grid';
import React, { Children, FunctionComponent, ReactNode } from 'react';
import pure from 'recompose/pure';
import { convertNodeToArray } from '../../../utils/react';
import {
  ColumnLabelText,
  ColumnLabelTextProps,
} from '../TextStyles/ColumnLabelText';
import {
  ColumnMainText,
  ColumnMainTextProps,
} from '../TextStyles/ColumnMainText';
import {
  ColumnSecondaryText,
  ColumnSecondaryTextProps,
} from '../TextStyles/ColumnSecondaryText';

interface OwnProps extends GridProps {
  /**
   * Label that is displayed at top of column
   */
  label: ReactNode;

  /**
   * Second line of text (typically, large black font)
   */
  mainText: ReactNode;

  /**
   * Any number of elements that will be wrapped with a Typography component to style it with a light-grey font
   */
  secondaryText?: ReactNode;

  /**
   * Props that can be applied to the Label text component
   */
  ColumnLabelTextProps?: ColumnLabelTextProps;

  /**
   * Props that can be applied to the Main text component
   */
  ColumnMainTextProps?: ColumnMainTextProps;

  /**
   * Props that can be applied to the secondaryText components
   */
  ColumnSecondaryTextProps?: ColumnSecondaryTextProps;
}

export interface LabeledColumnProps extends OwnProps {}

const LabeledColumnBase: FunctionComponent<LabeledColumnProps> = ({
  label,
  mainText,
  secondaryText,
  ColumnLabelTextProps: columnLabelTextProps,
  ColumnMainTextProps: columnMainTextProps,
  ColumnSecondaryTextProps: columnSecondaryTextProps,
  ...props
}) => {
  const secondary = convertNodeToArray(secondaryText);

  return (
    <Grid container item xs zeroMinWidth {...props}>
      <Grid item xs zeroMinWidth>
        <ColumnLabelText {...columnLabelTextProps}>{label}</ColumnLabelText>
        <ColumnMainText {...columnMainTextProps}>{mainText}</ColumnMainText>
        {!!secondary &&
          Children.map(secondary, text => {
            if (!text) return;
            return (
              <ColumnSecondaryText {...columnSecondaryTextProps}>
                {text}
              </ColumnSecondaryText>
            );
          })}
      </Grid>
    </Grid>
  );
};

export const LabeledColumn = pure(LabeledColumnBase);

LabeledColumn.displayName = 'LabeledColumn';
